import type { ComboboxItem } from '@mantine/core';
import { useEffect } from 'react';
import { isDefined, isNonNullish } from 'remeda';
import { fixUtf8StringEncoding } from 'shared/utils';

import type { CsvData, MatchedColumns } from '../types.ts';

/**
 * This hook attempts to match the columns in the CSV data with the available fields once the csv data has been loaded
 */
export function useAttemptToMatchColumns(
  data: CsvData | undefined,
  allFields: Array<ComboboxItem>,
  matchedColumns: MatchedColumns,
  setMatchedColumns: (value: MatchedColumns) => void,
) {
  useEffect(() => {
    if (!isDefined(data)) return;
    setMatchedColumns(computeMatchedColumns(data, allFields, matchedColumns));
  }, [isDefined(data)]);
}

/**
 * Tries to match the fields that we can import to the column names in the CSV data.
 * Note that the matching is purely order-based.
 * @param data The loaded CSV data
 * @param allFields All the available fields to match against
 * @param matchedColumns The currently matched columns, as saved in localStorage
 * @returns A new array of matched columns
 */
function computeMatchedColumns(
  data: CsvData,
  allFields: Array<ComboboxItem>,
  matchedColumns: MatchedColumns,
): MatchedColumns {
  return data[0].reduce<MatchedColumns>((memo, columnName, idx) => {
    const maybeMatchedField = allFields.find(
      field => field.label === fixUtf8StringEncoding(columnName),
    );

    if (isDefined(maybeMatchedField)) memo.push(maybeMatchedField.value);
    else if (isNonNullish(matchedColumns[idx])) memo.push(matchedColumns[idx]);
    else memo.push(null);

    return memo;
  }, []);
}
